import React, { useMemo } from 'react';
import { Box, Card, CardContent, Typography } from '@mui/material';
import { formatNumber } from '../../../utils/reportHelpers';
import {
  AnalyticsSummary,
  CATEGORY_COLORS,
  categoryLabel,
  statusLabel,
} from './types';

interface SummaryCardsProps {
  summary: AnalyticsSummary;
  translate: (key: string, options?: Record<string, unknown>) => string;
}

const headlineCardSx = {
  height: '100%',
  borderRadius: 3,
  boxShadow: 'none',
  border: '1px solid',
  borderColor: 'divider',
};

const breakdownRowSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 1,
  py: 0.75,
  borderBottom: '1px solid',
  borderColor: 'divider',
  '&:last-of-type': { borderBottom: 'none' },
};

// Summary cards: 4 headline cards (total / refused+rate / handoffs / and the category/status
// breakdown cards below). All derivations (refusal rate, sorting) are client-side; no fabricated
// fallbacks — a genuinely-absent status bucket is 0, divide-by-zero shows 0%.
export const SummaryCards: React.FC<SummaryCardsProps> = ({ summary, translate }) => {
  const refused = useMemo(
    () => summary.byStatus.find((s) => s.status === 'refused')?.count ?? 0,
    [summary.byStatus],
  );
  const refusalRate = summary.total > 0 ? refused / summary.total : 0;
  const refusalRatePct = `${formatNumber(refusalRate * 100, 1)}%`;

  const sortedCategories = useMemo(
    () => [...summary.byCategory].sort((a, b) => b.count - a.count),
    [summary.byCategory],
  );
  const sortedStatuses = useMemo(
    () => [...summary.byStatus].sort((a, b) => b.count - a.count),
    [summary.byStatus],
  );

  return (
    <Box sx={{ mb: { xs: 2, sm: 3 } }}>
      {/* Headline cards */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
          gap: { xs: 2, sm: 3 },
          mb: { xs: 2, sm: 3 },
        }}
      >
        <Card sx={headlineCardSx}>
          <CardContent>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
              {translate('aiTroubleshooting.analytics.cards.totalInvestigations', {
                _: 'Total investigations',
              })}
            </Typography>
            <Typography sx={{ fontSize: { xs: '1.75rem', sm: '2.125rem' }, fontWeight: 700, color: '#1A1A1A' }}>
              {formatNumber(summary.total, 0)}
            </Typography>
          </CardContent>
        </Card>

        <Card sx={headlineCardSx}>
          <CardContent>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
              {translate('aiTroubleshooting.analytics.cards.refused', { _: 'Refused' })}
            </Typography>
            <Typography
              data-testid="refused-count"
              sx={{ fontSize: { xs: '1.75rem', sm: '2.125rem' }, fontWeight: 700, color: '#1A1A1A' }}
            >
              {formatNumber(refused, 0)}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {translate('aiTroubleshooting.analytics.cards.refusalRate', { _: 'Refusal rate' })}:{' '}
              <Box component="span" data-testid="refusal-rate" sx={{ fontWeight: 600 }}>
                {refusalRatePct}
              </Box>
            </Typography>
          </CardContent>
        </Card>

        <Card sx={headlineCardSx}>
          <CardContent>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
              {translate('aiTroubleshooting.analytics.cards.handoffs', { _: 'Handoffs' })}
            </Typography>
            <Typography sx={{ fontSize: { xs: '1.75rem', sm: '2.125rem' }, fontWeight: 700, color: '#1A1A1A' }}>
              {formatNumber(summary.handoffCount, 0)}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Breakdown cards */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
          gap: { xs: 2, sm: 3 },
        }}
      >
        <Card sx={headlineCardSx}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5 }}>
              {translate('aiTroubleshooting.analytics.cards.byCategory', { _: 'By category' })}
            </Typography>
            {sortedCategories.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {translate('aiTroubleshooting.analytics.empty', { _: 'No data for this period.' })}
              </Typography>
            ) : (
              sortedCategories.map((row, index) => (
                <Box key={`${row.category ?? 'null'}`} sx={breakdownRowSx}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                    <Box
                      component="span"
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        backgroundColor: CATEGORY_COLORS[index % CATEGORY_COLORS.length],
                        flexShrink: 0,
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                    >
                      {categoryLabel(translate, row.category)}
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {formatNumber(row.count, 0)}
                  </Typography>
                </Box>
              ))
            )}
          </CardContent>
        </Card>

        <Card sx={headlineCardSx}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5 }}>
              {translate('aiTroubleshooting.analytics.cards.byStatus', { _: 'By status' })}
            </Typography>
            {sortedStatuses.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {translate('aiTroubleshooting.analytics.empty', { _: 'No data for this period.' })}
              </Typography>
            ) : (
              sortedStatuses.map((row, index) => (
                <Box key={`${row.status}`} sx={breakdownRowSx}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                    <Box
                      component="span"
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        backgroundColor: CATEGORY_COLORS[index % CATEGORY_COLORS.length],
                        flexShrink: 0,
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                    >
                      {statusLabel(translate, row.status)}
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {formatNumber(row.count, 0)}
                  </Typography>
                </Box>
              ))
            )}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default SummaryCards;
